import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();

/**
 * Payment method enum - mirrors PaymentMethodType from backend model
 */
export const SupplierPaymentMethodSchema = z.enum([
  "ach",
  "wire",
  "check",
  "card",
  "fednow",
  "credit_card",
  "other",
]);

/**
 * Supplier Payment Details response schema.
 * Bank account/routing numbers are returned masked (last 4 digits only).
 */
export const SupplierPaymentDetailsSchema = z.object({
  id: UUIDSchema,
  supplierId: UUIDSchema,
  method: SupplierPaymentMethodSchema,
  isDefault: z.boolean(),
  label: z.string().nullable(),
  bankName: z.string().nullable(),
  /** Last 4 digits only, e.g. "****1234" */
  accountNumberMasked: z.string().nullable(),
  /** Last 4 digits only, e.g. "****5678" */
  routingNumberMasked: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ListSupplierPaymentDetailsInputSchema = z.object({
  supplierId: UUIDSchema,
});

export const ListSupplierPaymentDetailsResponseSchema = z.object({
  paymentDetails: z.array(SupplierPaymentDetailsSchema),
});

// ---------------------------------------------------------------------------
// Get
// ---------------------------------------------------------------------------

export const GetSupplierPaymentDetailsInputSchema = z.object({
  id: UUIDSchema,
});

export const GetSupplierPaymentDetailsResponseSchema =
  SupplierPaymentDetailsSchema;

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const CreateSupplierPaymentDetailsInputSchema = z.object({
  supplierId: UUIDSchema,
  method: SupplierPaymentMethodSchema,
  isDefault: z.boolean().optional(),
  label: z.string().optional(),
  bankName: z.string().optional(),
  /** Plaintext account number - will be encrypted server-side */
  accountNumber: z
    .string()
    .regex(/^\d+$/, "Account number must contain only digits")
    .optional(),
  /** Plaintext routing number - will be encrypted server-side */
  routingNumber: z
    .string()
    .regex(/^\d{9}$/, "Routing number must be exactly 9 digits")
    .optional(),
});

export const CreateSupplierPaymentDetailsResponseSchema =
  SupplierPaymentDetailsSchema;

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

// NOTE: account/routing numbers are intentionally NOT editable. Bank numbers
// are immutable once created — to change them, delete this record and create a
// new one. This prevents silently redirecting funds on an existing record.
export const UpdateSupplierPaymentDetailsInputSchema = z.object({
  id: UUIDSchema,
  method: SupplierPaymentMethodSchema.optional(),
  isDefault: z.boolean().optional(),
  label: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
});

export const UpdateSupplierPaymentDetailsResponseSchema =
  SupplierPaymentDetailsSchema;

// ---------------------------------------------------------------------------
// Delete (soft delete)
// ---------------------------------------------------------------------------

export const DeleteSupplierPaymentDetailsInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteSupplierPaymentDetailsResponseSchema = z.object({
  success: z.boolean(),
});

// ---------------------------------------------------------------------------
// Contract router
// ---------------------------------------------------------------------------

export const supplierPaymentDetailsContracts = {
  list: oc
    .input(ListSupplierPaymentDetailsInputSchema)
    .output(ListSupplierPaymentDetailsResponseSchema)
    .route({ method: "GET" }),

  get: oc
    .input(GetSupplierPaymentDetailsInputSchema)
    .output(GetSupplierPaymentDetailsResponseSchema)
    .route({ method: "GET" }),

  create: oc
    .input(CreateSupplierPaymentDetailsInputSchema)
    .output(CreateSupplierPaymentDetailsResponseSchema)
    .route({ method: "POST" }),

  update: oc
    .input(UpdateSupplierPaymentDetailsInputSchema)
    .output(UpdateSupplierPaymentDetailsResponseSchema)
    .route({ method: "POST" }),

  delete: oc
    .input(DeleteSupplierPaymentDetailsInputSchema)
    .output(DeleteSupplierPaymentDetailsResponseSchema)
    .route({ method: "POST" }),
} as const;

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

export type SupplierPaymentDetails = z.infer<
  typeof SupplierPaymentDetailsSchema
>;
export type ListSupplierPaymentDetailsInput = z.infer<
  typeof ListSupplierPaymentDetailsInputSchema
>;
export type ListSupplierPaymentDetailsResponse = z.infer<
  typeof ListSupplierPaymentDetailsResponseSchema
>;
export type GetSupplierPaymentDetailsInput = z.infer<
  typeof GetSupplierPaymentDetailsInputSchema
>;
export type CreateSupplierPaymentDetailsInput = z.infer<
  typeof CreateSupplierPaymentDetailsInputSchema
>;
export type UpdateSupplierPaymentDetailsInput = z.infer<
  typeof UpdateSupplierPaymentDetailsInputSchema
>;
export type DeleteSupplierPaymentDetailsInput = z.infer<
  typeof DeleteSupplierPaymentDetailsInputSchema
>;
